/**
 * Admin modal for diagnosing why a specific customer can't log in.
 * Given the email or phone the customer says they're using, we call
 * POST /api/admin/diagnose-login which returns Hub allowlist status,
 * local portal_customer row, recent OTP codes, delivery logs, and
 * rate-limit windows. The modal renders the raw report + a short
 * recommendations list at the top so the admin knows exactly what to
 * do next.
 */
import { useState } from 'react';
import {
  X, Search, Loader2, CheckCircle2, AlertCircle, Bug,
} from 'lucide-react';

import { adminApi } from '@/lib/api';

function StatusPill({ ok, children }) {
  const cls = ok
    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
    : 'bg-red-50 border border-red-200 text-red-800';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${cls} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
      {children}
    </span>
  );
}

export default function AdminDiagnoseLoginModal({ initialContact = '', onClose }) {
  const [contact, setContact] = useState(initialContact);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const run = async (e) => {
    e?.preventDefault?.();
    if (!contact.trim() || busy) return;
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const { data } = await adminApi.diagnoseLogin(contact.trim());
      setReport(data);
    } catch (err) {
      setError(err?.detail || 'Diagnostic failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="admin-diagnose-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EDE5D4]">
          <div className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-[#0B1930]" />
            <h2 className="font-heading uppercase tracking-wide text-base text-[#0B1930]">
              Diagnose Login
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#4B5563] hover:text-[#0B1930] disabled:opacity-40"
            disabled={busy}
            data-testid="admin-diagnose-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 space-y-4">
          <form onSubmit={run} className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
              <input
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="Email or phone the customer is trying (e.g. name@company.com or +17735551234)"
                className="w-full rounded-full bg-white border border-[#EDE5D4] pl-9 pr-4 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                data-testid="admin-diagnose-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !contact.trim()}
              className="rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-60"
              data-testid="admin-diagnose-run"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Run'}
            </button>
          </form>

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-3 py-2 text-sm flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {report && (
            <div className="space-y-4 text-sm" data-testid="admin-diagnose-report">
              {/* Recommendations */}
              <section>
                <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                  Recommendations
                </h3>
                <ul className="space-y-1">
                  {report.recommendations.map((r, i) => (
                    <li key={i} className="rounded-xl bg-[#FAF7F0] border border-[#EDE5D4] px-3 py-2 text-[#0B1930]">
                      {r}
                    </li>
                  ))}
                </ul>
              </section>

              {/* Hub */}
              <section>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563]">
                    Hub Authorization
                  </h3>
                  {report.hub.authorized === true && <StatusPill ok>Authorized</StatusPill>}
                  {report.hub.authorized === false && <StatusPill ok={false}>Not on Hub</StatusPill>}
                  {report.hub.authorized === null && <StatusPill ok={false}>Hub error</StatusPill>}
                </div>
                <div className="rounded-xl border border-[#EDE5D4] px-3 py-2 space-y-1 text-[12px] text-[#0B1930]">
                  <p><span className="text-[#4B5563]">Contact type:</span> {report.contact_type}</p>
                  <p><span className="text-[#4B5563]">Normalized:</span> <code className="bg-[#F5EFE1] px-1 rounded">{report.normalized_contact}</code></p>
                  {report.hub.business_name && <p><span className="text-[#4B5563]">Business:</span> {report.hub.business_name}</p>}
                  {report.hub.zoho_customer_id && <p><span className="text-[#4B5563]">Zoho ID:</span> {report.hub.zoho_customer_id}</p>}
                  {report.hub.reason && <p className="text-red-700">{report.hub.reason}</p>}
                  {report.hub.error && <p className="text-red-700">Error: {report.hub.error}</p>}
                </div>
              </section>

              {/* Local */}
              <section>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563]">
                    Local portal_customer
                  </h3>
                  {report.local_customer.found
                    ? <StatusPill ok>Found</StatusPill>
                    : <StatusPill ok={false}>Missing</StatusPill>}
                </div>
                <div className="rounded-xl border border-[#EDE5D4] px-3 py-2 text-[12px] text-[#0B1930] space-y-1">
                  {report.local_customer.found ? (
                    <>
                      <p><span className="text-[#4B5563]">Portal id:</span> {report.local_customer.portal_customer_id}</p>
                      <p><span className="text-[#4B5563]">Display:</span> {report.local_customer.display_name}</p>
                      <p><span className="text-[#4B5563]">Emails on file:</span> {(report.local_customer.verified_emails || []).join(', ') || '—'}</p>
                      <p><span className="text-[#4B5563]">Phones on file:</span> {(report.local_customer.verified_phones || []).join(', ') || '—'}</p>
                      {report.local_customer.is_opted_out && (
                        <p className="text-red-700 font-semibold">Phone is opted-out (STOP received).</p>
                      )}
                      <p><span className="text-[#4B5563]">Last login:</span> {report.local_customer.last_login_at || 'never'}</p>
                      <p><span className="text-[#4B5563]">Provisioning:</span> {report.local_customer.hub_provisioned ? 'Hub' : 'JIT-on-sign-in'}</p>
                    </>
                  ) : (
                    <p className="text-[#4B5563]">
                      No portal_customer row for this contact. If Hub authorization is OK, the
                      row is created on first sign-in — no admin action needed.
                    </p>
                  )}
                </div>
              </section>

              {/* Deliveries */}
              <section>
                <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                  Recent deliveries ({report.recent_deliveries.length})
                </h3>
                {report.recent_deliveries.length === 0 ? (
                  <p className="text-[12px] text-[#9CA3AF] italic">No delivery attempts logged for this contact yet.</p>
                ) : (
                  <div className="rounded-xl border border-[#EDE5D4] divide-y divide-[#EDE5D4] overflow-hidden">
                    {report.recent_deliveries.map((d, i) => (
                      <div key={i} className="px-3 py-2 text-[12px] flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[#0B1930]">
                            {d.provider || '—'} · {d.channel || d.purpose || '—'}
                          </p>
                          <p className="text-[10px] text-[#9CA3AF]">{d.created_at}</p>
                          {d.error && <p className="text-[11px] text-red-700 mt-0.5">{d.error}</p>}
                        </div>
                        <StatusPill ok={(d.status || '').toLowerCase() === 'sent' || (d.status || '').toLowerCase() === 'delivered'}>
                          {d.status || 'unknown'}
                        </StatusPill>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* OTP codes */}
              <section>
                <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                  Recent OTP codes ({report.recent_otp_codes.length})
                </h3>
                {report.recent_otp_codes.length === 0 ? (
                  <p className="text-[12px] text-[#9CA3AF] italic">No OTP codes created for this contact yet.</p>
                ) : (
                  <div className="rounded-xl border border-[#EDE5D4] divide-y divide-[#EDE5D4] overflow-hidden">
                    {report.recent_otp_codes.map((o, i) => (
                      <div key={i} className="px-3 py-2 text-[12px] flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[#0B1930]">
                            {o.channel || '—'} · {o.purpose}
                          </p>
                          <p className="text-[10px] text-[#9CA3AF]">
                            Created {o.created_at} · expires {o.expires_at}
                          </p>
                        </div>
                        <div className="text-right text-[11px] text-[#4B5563]">
                          Attempts {o.attempts_used}/{o.max_attempts}
                          <p className={`text-[10px] font-bold uppercase mt-0.5 ${o.consumed ? 'text-emerald-700' : 'text-[#4B5563]'}`}>
                            {o.consumed ? 'consumed' : 'pending'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Rate limits */}
              {report.rate_limits.length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-wide text-[#4B5563] mb-1">
                    Rate limit windows
                  </h3>
                  <div className="rounded-xl border border-[#EDE5D4] divide-y divide-[#EDE5D4] overflow-hidden">
                    {report.rate_limits.map((r, i) => (
                      <div key={i} className="px-3 py-2 text-[12px] flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[#0B1930]">{r.bucket}</p>
                          <p className="text-[10px] text-[#9CA3AF]">since {r.window_start}</p>
                        </div>
                        <div className="text-right text-[11px] text-[#4B5563]">
                          {r.count}/{r.limit ?? '∞'}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-[#EDE5D4] bg-[#FAF7F0]">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors"
            data-testid="admin-diagnose-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
